import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import SearchableSelect from "../ui/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import {
  DollarSign, TrendingUp, Briefcase, Building2, Wrench, Download, ExternalLink,
  FileDown, Loader2, CheckCircle2, Clock, Calendar, ChevronDown, ChevronRight,
  Handshake, Undo2, Receipt
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { toast } from "sonner";
import StatCard from "./StatCard";

const JobSourceReportTab = ({
  jobSourceData, sourceWeeklyData, selectedSource, setSelectedSource,
  formatCurrency, formatDate, exportToPDF, exportToCSV, exportingPdf,
  fetchReportData
}) => {
  const { isDark } = useTheme();
  if (!jobSourceData) return null;

  const { summary, jobs, job_sources } = jobSourceData;
  const profitColor = isDark ? "text-[#FFCC00]" : "text-amber-600";
  const profitBg = isDark ? "bg-[#FFCC00]/10" : "bg-amber-100";
  const linkColor = isDark ? "text-[#FFCC00] hover:text-[#FFCC00]/80" : "text-blue-600 hover:text-blue-800";
  const btnOutline = isDark ? "border-white/20 text-foreground hover:bg-theme-hover" : "border-gray-300 text-gray-700 hover:bg-gray-100";
  const sourcePdfBtn = isDark ? "border-[#0046BE]/50 text-[#0046BE] hover:bg-[#0046BE]/10" : "border-blue-500/50 text-blue-700 hover:bg-blue-50";

  return (
    <div className="space-y-6">
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground text-sm">Job Source:</span>
        </div>
        {/* Searchable Job-Source picker — same swap as TechnicianReportTab.
            Owners with 10+ sources (referral partners, dispatch services,
            etc.) can now type to filter instead of scrolling. */}
        <SearchableSelect
          value={selectedSource}
          onChange={setSelectedSource}
          options={[
            { value: "all", label: "All Sources" },
            ...(job_sources?.map(s => ({ value: s.source_id, label: s.name })) || []),
          ]}
          placeholder="All Sources"
          searchThreshold={4}
          className="w-[200px]"
          triggerClassName="bg-theme-surface border-theme text-foreground h-10"
          data-testid="source-filter"
        />
      </div>

      {/* Responsive grid: 5 cards. "Source Commission" + "Company Profit"
          labels need breathing room on tablets — keep 3 cols at lg, only
          line up as 5 at xl ≥ 1280 px. */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        <StatCard title="Total Jobs" value={summary.total_jobs} icon={Briefcase} color="text-blue-500" bgColor="bg-blue-500/10" />
        <StatCard title="Total Revenue" value={formatCurrency(summary.total_revenue)} icon={DollarSign} color="text-green-600" bgColor="bg-green-500/10" />
        <StatCard title="Parts Cost" value={formatCurrency(summary.total_parts)} icon={Wrench} color="text-orange-500" bgColor="bg-orange-500/10" />
        <StatCard title="Source Commission" value={formatCurrency(summary.total_source_commission)} icon={Building2} color="text-cyan-600" bgColor="bg-cyan-500/10" />
        <StatCard title="Company Profit" value={formatCurrency(summary.total_company_profit)} icon={TrendingUp} color={profitColor} bgColor={profitBg} />
        {(summary.total_tax_charged || 0) > 0 && (
          <>
            <StatCard title="Tax Charged" value={formatCurrency(summary.total_tax_charged)} icon={Receipt} color="text-teal-600" bgColor="bg-teal-500/10" data-testid="source-tax-charged-card" />
            <StatCard title="Tax Collected" value={formatCurrency(summary.total_tax_collected)} icon={Receipt} color="text-teal-600" bgColor="bg-teal-500/10" data-testid="source-tax-collected-card" />
          </>
        )}
      </div>

      <Card className="glass-card border-theme">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-foreground text-base font-['Manrope']">Job Source Details</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => exportToPDF('job-source')} disabled={exportingPdf === 'job-source'} className={sourcePdfBtn} data-testid="export-source-pdf-btn">
              {exportingPdf === 'job-source' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportToCSV(jobs, "job_source_report")} className={btnOutline} data-testid="export-source-btn">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-theme hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Job ID</TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">Address</TableHead>
                  <TableHead className="text-muted-foreground">ZIP</TableHead>
                  <TableHead className="text-muted-foreground">Job Type</TableHead>
                  <TableHead className="text-muted-foreground text-right">Total</TableHead>
                  <TableHead className="text-muted-foreground text-right" title="Sales tax on the job — excluded from commissions">Tax</TableHead>
                  <TableHead className="text-muted-foreground">Payment By</TableHead>
                  <TableHead className="text-muted-foreground text-right">Parts</TableHead>
                  <TableHead className="text-muted-foreground text-right">Source Comm.</TableHead>
                  <TableHead className="text-muted-foreground text-right">Co. Profit</TableHead>
                  <TableHead className="text-muted-foreground">Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.length === 0 ? (
                  <TableRow className="border-theme">
                    <TableCell colSpan={12} className="text-center text-muted-foreground py-8">No jobs from sources found for selected period</TableCell>
                  </TableRow>
                ) : (
                  jobs.map((job, i) => (
                    <TableRow key={job.job_id || i} className="border-theme hover:bg-theme-surface">
                      <TableCell>
                        <Link to={`/dashboard/jobs?job=${job.job_id}`} className={`${linkColor} flex items-center gap-1 font-mono text-xs`} data-testid={`source-job-link-${job.job_id}`}>
                          {job.job_id?.slice(-8) || "—"}<ExternalLink className="w-3 h-3" />
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(job.completed_at)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[150px] truncate">{job.address || "N/A"}</TableCell>
                      <TableCell className="text-muted-foreground">{job.zip || "—"}</TableCell>
                      <TableCell className="text-foreground">{job.job_type}</TableCell>
                      <TableCell className="text-green-600 text-right font-medium">{formatCurrency(job.total)}</TableCell>
                      <TableCell className="text-teal-600 text-right text-sm" data-testid={`source-tax-${job.job_id}`}>{(job.tax || 0) > 0 ? formatCurrency(job.tax) : "—"}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{job.payment_by}</TableCell>
                      <TableCell className="text-orange-500 text-right">{formatCurrency(job.parts)}</TableCell>
                      <TableCell className="text-cyan-600 text-right">{formatCurrency(job.source_commission)}</TableCell>
                      <TableCell className={`${profitColor} text-right font-semibold`}>{formatCurrency(job.company_commission)}</TableCell>
                      <TableCell className="text-foreground font-medium">{job.source_name || "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Weekly Source Settlements */}
      {sourceWeeklyData && (
        <SourceWeeklySettlements
          data={sourceWeeklyData}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          isDark={isDark}
          onRefresh={fetchReportData}
        />
      )}
    </div>
  );
};

export default JobSourceReportTab;

// ─── Weekly Source Settlements ──────────────────────────────────
const SourceWeeklySettlements = ({ data, formatCurrency, formatDate, isDark, onRefresh }) => {
  const [filter, setFilter] = useState("all"); // all, unsettled, settled, partial
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [settleDialog, setSettleDialog] = useState(null);
  const [settleAmount, setSettleAmount] = useState("");
  const [settleNotes, setSettleNotes] = useState("");
  const [settleRef, setSettleRef] = useState("");
  const [settling, setSettling] = useState(false);

  const { weeks, summary } = data;
  const filtered = weeks.filter(w => {
    if (filter === "settled") return w.settled;
    if (filter === "unsettled") return !w.settled && !w.partial;
    if (filter === "partial") return w.partial;
    return true;
  });

  const profitColor = isDark ? "text-[#FFCC00]" : "text-amber-600";

  const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  // Open the settle dialog and default the amount to the OUTSTANDING
  // balance (commission minus what's already been paid) so the common
  // case (settle the rest in one click) needs zero input from the user.
  const openSettleDialog = (week) => {
    const remaining = (week.remaining_amount ?? week.source_commission) || 0;
    setSettleDialog(week);
    setSettleAmount(remaining.toFixed(2));
    setSettleNotes("");
    setSettleRef("");
  };

  const handleSettle = async () => {
    if (!settleDialog) return;
    const amount = parseFloat(settleAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    const remaining = settleDialog.remaining_amount ?? settleDialog.source_commission;
    if (amount > remaining + 0.01) {
      toast.error(`Amount can't exceed the remaining balance (${formatCurrency(remaining)})`);
      return;
    }
    setSettling(true);
    try {
      await axios.post(`${API}/reports/job-source/settle`, {
        source_id: settleDialog.source_id,
        source_name: settleDialog.source_name,
        week_start: settleDialog.week_start,
        week_end: settleDialog.week_end,
        settled_amount: amount,
        commission_amount: settleDialog.source_commission,
        revenue_amount: settleDialog.revenue,
        jobs_count: settleDialog.jobs_count,
        notes: settleNotes,
        reference: settleRef,
      }, getAuth());
      const wasPartial = amount < remaining - 0.01;
      toast.success(wasPartial
        ? `Partial payment of ${formatCurrency(amount)} recorded`
        : "Week marked as paid");
      setSettleDialog(null);
      setSettleAmount("");
      setSettleNotes("");
      setSettleRef("");
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to settle");
    } finally { setSettling(false); }
  };

  const handleUnsettle = async (settlement_id, label) => {
    if (!settlement_id) return;
    if (!window.confirm(`Reopen ${label}?`)) return;
    try {
      await axios.delete(`${API}/reports/job-source/settle/${settlement_id}`, getAuth());
      toast.success("Settlement reopened");
      onRefresh?.();
    } catch { toast.error("Failed to reopen"); }
  };

  const toggleWeek = (key) => setExpandedWeeks(prev => ({ ...prev, [key]: !prev[key] }));

  const fmtWeek = (start, end) => {
    const s = new Date(start + "T00:00:00");
    const e = new Date(end + "T00:00:00");
    const mo = s.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const ed = e.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${mo} – ${ed}`;
  };

  return (
    <div className="space-y-4" data-testid="source-weekly-settlements">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="text-foreground font-semibold text-base flex items-center gap-2">
          <Handshake className="w-4 h-4 text-cyan-500" /> Weekly Source Settlements
        </h3>
        <div className="flex gap-2 flex-wrap">
          {["all", "unsettled", "partial", "settled"].map(f => {
            const counts = {
              all: weeks.length,
              unsettled: summary.unsettled_count,
              partial: summary.partial_count || 0,
              settled: summary.settled_count,
            };
            const labels = { all: "All", unsettled: "Unpaid", partial: "Partial", settled: "Paid" };
            return (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                  filter === f
                    ? isDark ? "bg-[#FFCC00]/15 text-[#FFCC00]" : "bg-amber-100 text-amber-700"
                    : "text-muted-foreground hover:text-foreground"
                }`} data-testid={`filter-${f}`}>
                {labels[f]} ({counts[f]})
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="glass-card border-theme"><CardContent className="p-3">
          <p className="text-muted-foreground text-[10px] uppercase tracking-wider">Total Commission</p>
          <p className={`text-lg font-bold ${profitColor}`} data-testid="total-commission">{formatCurrency(summary.total_commission)}</p>
        </CardContent></Card>
        <Card className="glass-card border-theme"><CardContent className="p-3">
          <p className="text-muted-foreground text-[10px] uppercase tracking-wider">Settled</p>
          <p className="text-lg font-bold text-green-500" data-testid="total-settled">{formatCurrency(summary.total_settled)}</p>
        </CardContent></Card>
        <Card className="glass-card border-theme"><CardContent className="p-3">
          <p className="text-muted-foreground text-[10px] uppercase tracking-wider">Outstanding</p>
          <p className="text-lg font-bold text-red-500" data-testid="total-unsettled">{formatCurrency(summary.total_unsettled)}</p>
        </CardContent></Card>
      </div>

      {/* Week cards */}
      {filtered.length === 0 ? (
        <Card className="glass-card border-theme"><CardContent className="py-10 text-center">
          <Handshake className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No source balances for this period</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(w => {
            const key = `${w.source_id}|${w.week_start}`;
            const isExpanded = expandedWeeks[key];
            return (
              <Card key={key} className="glass-card border-theme overflow-hidden" data-testid={`week-card-${key}`}>
                <button onClick={() => toggleWeek(key)}
                  className="w-full flex items-center gap-3 p-3 sm:p-4 hover:bg-theme-surface transition-colors text-left">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${w.settled ? "bg-green-500/10" : w.partial ? "bg-amber-500/10" : "bg-orange-500/10"}`}>
                    {w.settled ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : w.partial ? <Handshake className="w-4 h-4 text-amber-500" /> : <Clock className="w-4 h-4 text-orange-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-foreground font-semibold text-sm">{w.source_name}</span>
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${
                        w.settled ? "text-green-500 border-green-500/30 bg-green-500/10"
                        : w.partial ? "text-amber-500 border-amber-500/30 bg-amber-500/10"
                        : "text-orange-500 border-orange-500/30 bg-orange-500/10"
                      }`}>
                        {w.settled ? "Paid" : w.partial ? `Partial · ${formatCurrency(w.remaining_amount)} left` : "Unpaid"}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground text-xs mt-0.5 flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {fmtWeek(w.week_start, w.week_end)} · {w.jobs_count} job{w.jobs_count !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <div className="text-right shrink-0 mr-2">
                    <p className={`font-bold text-sm ${profitColor}`}>{formatCurrency(w.source_commission)}</p>
                    <p className="text-muted-foreground text-[10px]">commission</p>
                  </div>
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                </button>

                {isExpanded && (
                  <div className={`border-t px-4 py-3 space-y-3 ${isDark ? "border-white/[0.04] bg-white/[0.01]" : "border-gray-100 bg-gray-50/50"}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div><p className="text-muted-foreground text-[10px] uppercase">Revenue</p><p className="text-foreground font-medium">{formatCurrency(w.revenue)}</p></div>
                      <div><p className="text-muted-foreground text-[10px] uppercase">Parts</p><p className="text-foreground font-medium">{formatCurrency(w.parts)}</p></div>
                      <div><p className="text-muted-foreground text-[10px] uppercase">Commission</p><p className={`font-semibold ${profitColor}`}>{formatCurrency(w.source_commission)}</p></div>
                      <div><p className="text-muted-foreground text-[10px] uppercase">Co. Profit</p><p className="text-foreground font-medium">{formatCurrency(w.company_profit)}</p></div>
                    </div>

                    {/* Settlement history — one row per partial payment, each independently reopen-able */}
                    {(w.settlements || []).length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-muted-foreground text-[10px] uppercase tracking-wider font-medium">
                          Payments ({(w.settlements || []).length}) · {formatCurrency(w.settled_amount)} of {formatCurrency(w.source_commission)}
                        </p>
                        {(w.settlements || []).map((s) => (
                          <div key={s.settlement_id} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm ${isDark ? "bg-white/[0.02]" : "bg-white"}`} data-testid={`settlement-row-${s.settlement_id}`}>
                            <div className="min-w-0 flex-1">
                              <p className="text-foreground font-medium flex items-center gap-1.5">
                                <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                                {formatCurrency(s.settled_amount)}
                              </p>
                              <p className="text-muted-foreground text-[11px] truncate">
                                {s.settled_by} · {formatDate(s.settled_at)}
                                {s.notes && ` · ${s.notes}`}
                                {s.reference && ` · Ref: ${s.reference}`}
                              </p>
                            </div>
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-red-500 shrink-0"
                              onClick={(e) => { e.stopPropagation(); handleUnsettle(s.settlement_id, `payment of ${formatCurrency(s.settled_amount)}`); }}
                              data-testid={`unsettle-${s.settlement_id}`}>
                              <Undo2 className="w-3 h-3 mr-1" /> Reopen
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {!w.settled && (
                      <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5"
                        onClick={(e) => { e.stopPropagation(); openSettleDialog(w); }}
                        data-testid={`settle-${key}`}>
                        <Handshake className="w-3.5 h-3.5" />
                        {w.partial ? `Add Payment (${formatCurrency(w.remaining_amount)} remaining)` : "Record Payment"}
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Settle Dialog */}
      <Dialog open={!!settleDialog} onOpenChange={(open) => { if (!open) { setSettleDialog(null); setSettleAmount(""); setSettleNotes(""); setSettleRef(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="settle-source-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Handshake className="w-5 h-5 text-cyan-500" /> Record Payment
            </DialogTitle>
          </DialogHeader>
          {settleDialog && (() => {
            const commission = settleDialog.source_commission || 0;
            const alreadyPaid = settleDialog.settled_amount || 0;
            const remaining = settleDialog.remaining_amount ?? commission;
            const enteredAmt = parseFloat(settleAmount) || 0;
            const wouldRemainOpen = enteredAmt > 0 && enteredAmt < remaining - 0.01;
            return (
              <div className="space-y-4">
                <div className={`rounded-lg p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-50 border border-gray-200"}`}>
                  <p className="text-foreground font-semibold">{settleDialog.source_name}</p>
                  <p className="text-muted-foreground text-sm">{fmtWeek(settleDialog.week_start, settleDialog.week_end)}</p>
                  <div className="grid grid-cols-3 gap-3 mt-3 text-sm">
                    <div><p className="text-muted-foreground text-[10px]">Commission</p><p className={`font-bold ${profitColor}`}>{formatCurrency(commission)}</p></div>
                    <div><p className="text-muted-foreground text-[10px]">Already Paid</p><p className="text-green-500 font-medium">{formatCurrency(alreadyPaid)}</p></div>
                    <div><p className="text-muted-foreground text-[10px]">Remaining</p><p className="text-orange-500 font-bold">{formatCurrency(remaining)}</p></div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-muted-foreground">Payment amount <span className="text-red-500">*</span></label>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => setSettleAmount((remaining / 2).toFixed(2))}
                        className="text-[10px] px-2 py-0.5 rounded bg-muted hover:bg-muted/80 text-foreground"
                        data-testid="preset-half">Half</button>
                      <button type="button" onClick={() => setSettleAmount(remaining.toFixed(2))}
                        className="text-[10px] px-2 py-0.5 rounded bg-muted hover:bg-muted/80 text-foreground"
                        data-testid="preset-full">Full ({formatCurrency(remaining)})</button>
                    </div>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <Input
                      type="number" step="0.01" min="0.01" max={remaining}
                      value={settleAmount}
                      onChange={e => setSettleAmount(e.target.value)}
                      className="pl-7 text-base font-semibold"
                      placeholder="0.00"
                      data-testid="settle-amount"
                      autoFocus
                    />
                  </div>
                  {wouldRemainOpen && (
                    <p className="text-amber-500 text-[11px] mt-1">
                      ⚠ This is a partial payment — {formatCurrency(remaining - enteredAmt)} will remain open.
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-xs text-muted-foreground">Notes (optional)</label>
                  <Input value={settleNotes} onChange={e => setSettleNotes(e.target.value)} placeholder="e.g., Paid via check #1234" className="mt-1" data-testid="settle-notes" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Reference # (optional)</label>
                  <Input value={settleRef} onChange={e => setSettleRef(e.target.value)} placeholder="e.g., CHK-1234" className="mt-1" data-testid="settle-ref" />
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettleDialog(null)}>Cancel</Button>
            <Button onClick={handleSettle} disabled={settling} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="confirm-settle">
              {settling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Handshake className="w-4 h-4 mr-1" />}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
